import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import utils from "../../utils/utils";
import * as md5 from "js-md5";
import { GlobalDataType } from "./types";
import { useStartupData } from "../../modules";
import { PROXY_ACTIVATED_EVENT } from "../../modules/domainGuard";

// 类型定义
interface GlobalDataContextType {
  data: GlobalDataType;
  isInitialized: boolean;
  loadData: (params: {
    url: string;
    cdn?: boolean;
    body?: any;
    options?: any;
  }) => Promise<any>;
  clearData: () => void;
  // 便捷属性
  changeTime: number;
  cacheData: Record<string, any>;
}

interface GlobalDataContextProviderProps {
  children: React.ReactNode;
}

// 创建 Context
const GlobalDataContext = createContext<GlobalDataContextType | null>(null);

// 运行锁映射
const runMap: Record<string, boolean> = {};

// 同步运行函数
export function syncRunning(
  key: string,
  runnable: () => Promise<any>
): Promise<any> {
  return new Promise(resolve => {
    const run = async () => {
      try {
        while (runMap[key]) {
          await utils.sleep(100);
        }
        runMap[key] = true;
        const ret = await runnable();
        resolve(ret);
      } finally {
        delete runMap[key];
      }
    };
    run();
  });
}
// 创建全局数据对象
const createGlobalData = (): GlobalDataType => {
  let changeTime = 0;
  let data: Record<string, any> = {};
  const listeners: Array<(data: GlobalDataType) => void> = [];

  return {
    get changeTime() {
      return changeTime;
    },
    get data() {
      return data;
    },
    isDataInitialized() {
      return Object.keys(data).length > 0;
    },
    addListener(callback: (data: GlobalDataType) => void) {
      listeners.push(callback);
    },
    removeListener(callback: (data: GlobalDataType) => void) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    },
    // 内部方法用于更新数据
    _updateData(newData: Record<string, any>) {
      data = { ...data, ...newData };
      changeTime = utils.getServerTime();
      listeners.forEach(listener => listener(this));
    },
  };
};

const globalData = createGlobalData();
// Provider 组件
export function GlobalDataContextProvider({
  children,
}: GlobalDataContextProviderProps) {
  const [data, setData] = useState<GlobalDataType>(globalData);
  const [isInitialized, setIsInitialized] = useState(() =>
    globalData.isDataInitialized()
  );

  // 监听数据变化
  useEffect(() => {
    const handleDataChange = (newData: GlobalDataType) => {
      setData(newData);
      setIsInitialized(true);
    };

    globalData.addListener(handleDataChange);

    return () => {
      globalData.removeListener(handleDataChange);
    };
  }, []);

  // 在组件顶层调用useStartupData
  const { data: startupData } = useStartupData();

  const loadData = useCallback(
    async ({
      url,
      cdn,
      body,
      options,
    }: {
      url: string;
      cdn?: boolean;
      body?: any;
      options?: any;
    }) => {
      if (!startupData) {
        return;
      }
      const key = md5.md5(JSON.stringify({ url, body }));
      const r = await syncRunning("lock-" + key, async () => {
        const cachedData = globalData.data[key];
        if (!cachedData) {
          let resp;
          if (cdn) {
            resp = await utils.getAsCache(url, startupData);
          } else {
            if (!url.startsWith("/api")) {
              url = "/api" + url;
            }
            resp = await utils.request(url, body, options);
          }
          globalData._updateData({ [key]: resp.data });
          return resp.data;
        }
        return globalData.data[key];
      });
      return r;
    },
    [data.changeTime, startupData]
  );

  const clearData = useCallback(() => {
    globalData._updateData({});
  }, []);

  // 域名容灾「软恢复」：主域名运行中失效、代理切到备用域名后，domainGuard 派发该事件。
  // bump changeTime（_updateData 会刷新 changeTime 并通知）→ 所有 useGlobalRemoteList 消费者
  // 的 loadData 重新执行：首屏失败、未写缓存的项（如游戏列表/分类）真正重新请求、经代理走
  // 备用域名成功；已成功缓存的项命中缓存不重复拉。就地恢复，不整页 reload、不打断游戏。
  useEffect(() => {
    const onProxyActivated = () => {
      clearData();
    };
    window.addEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    return () => {
      window.removeEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    };
  }, [clearData]);

  const value = useMemo(
    () => ({
      data,
      isInitialized,
      loadData,
      clearData,
      changeTime: data.changeTime,
      cacheData: data.data,
    }),
    [data, isInitialized, loadData, clearData]
  );

  return (
    <GlobalDataContext.Provider value={value}>
      {children}
    </GlobalDataContext.Provider>
  );
}

export default GlobalDataContext;
